import React, { useEffect, useState } from 'react'
import { getEntries, getFacets } from '../audit-api'
import { locationText } from '../entry-format'
import { formatAgo, formatCount, formatDateTime } from '../../sso-workspace/format'
import { DayBars } from './day-bars'
import '../audit.scss'

const DAY = 864e5
const FAILED = '—'

// Drop the keys a scope doesn't use (the group scope has no lens).
const clean = p => Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined && v !== null && v !== ''))

const sum = list => (list || []).reduce((n, f) => n + (f.count || 0), 0)
const countOf = (list, key) => ((list || []).find(f => f.key === key) || { count: 0 }).count || 0

// The read's state: undefined while it runs, null after a failure.
const useRead = (read, key) => {
  const [value, setValue] = useState(undefined)
  useEffect(() => {
    let live = true
    setValue(undefined)
    read().then(
      v => live && setValue(v || {}),
      () => live && setValue(null)
    )
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  return value
}

const Tile = ({ label, value, detail, children }) =>
  <div className="sw-tile" role="group" aria-label={label}>
    <span className="sw-k">{label}</span>
    {value !== undefined && <span className="sw-v">{value}</span>}
    {detail && <span className="sw-d">{detail}</span>}
    {children}
  </div>

// The Activity tab's summary (mockup "stats" card, spec §5): last sign-in
// (app and place), courses opened, changes made, apps used and a 30-day bar
// strip, from three reads of the read API in `base`'s scope ({ person, lens }
// or { group }). The sign-in read looks back a year; the others 30 days.
// A failed read shows a dash in its own tiles, never an error page.
export const SummaryStrip = ({ base }) => {
  const key = JSON.stringify(base)
  const [now] = useState(() => Date.now())
  const window30 = { from: new Date(now - 30 * DAY).toISOString(), to: new Date(now).toISOString() }

  const signin = useRead(() => getEntries(clean({ ...base, lens: base.lens && 'did', action: 'signin.succeeded', limit: 1, from: new Date(now - 365 * DAY).toISOString(), to: new Date(now).toISOString() })), key)
  const launches = useRead(() => getFacets(clean({ ...base, action: 'course.launched', includeViews: '1', ...window30 })), key)
  const facets = useRead(() => getFacets(clean({ ...base, ...window30, includeViews: '1' })), key)

  const last = signin && signin.items && signin.items[0]
  const lastValue = signin === undefined ? '' : signin === null ? FAILED : last ? <span title={formatDateTime(last.occurredAt)}>{formatAgo(last.occurredAt)}</span> : 'No sign-ins in the last year'
  const lastDetail = last ? [last.app, locationText(last.location)].filter(Boolean).join(' · ') : null

  // A facet read that hit the server's cap says so (truncated): its figures
  // are lower bounds, shown with a plus.
  const figure = (read, value) => (read === undefined ? '' : read === null ? FAILED : `${formatCount(value(read))}${read.truncated ? '+' : ''}`)
  const apps = (facets && facets.apps) || []

  return (
    <div className="sw-strip au-strip" role="region" aria-label="Summary">
      <Tile label={base.group ? 'Last sign-in by a member' : 'Last sign-in'} value={lastValue} detail={lastDetail} />
      {/* audit.views keeps one launch row per person, course and day, so this
          counts days with a course opened (owner ruling m4), not launches. */}
      <Tile label="Days with a course opened, 30 days" value={figure(launches, f => sum(f.categories))} detail={base.group ? 'Each member and course counts once a day' : 'Each course counts once a day'} />
      <Tile label="Changes made, 30 days" value={figure(facets, f => countOf(f.categories, 'changes'))} />
      <Tile label="Apps used" value={figure(facets, () => apps.length)} detail={apps.slice(0, 2).map(a => a.key).join(', ')} />
      <Tile label="Activity, 30 days">
        {facets === null
          ? <span className="sw-v">{FAILED}</span>
          : <DayBars days={(facets && facets.days) || []} from={window30.from} to={window30.to} timeZone={facets ? facets.timeZone : undefined} />}
      </Tile>
    </div>
  )
}
